/**
 * Theme store. Writes the `data-theme` attribute directly so a switch applies without
 * a re-render and without a flash of the previous palette.
 */

import { create } from "zustand";

export type Theme = "dark" | "light" | "glass";

const STORAGE_KEY = "quantlab.theme";

function readInitial(): Theme {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light" || stored === "glass") return stored;
    // Respect the OS preference on first run rather than assuming dark.
    return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
  } catch {
    return "dark";
  }
}

function apply(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  // Keeps the mobile browser chrome and the iOS status bar in step with the app.
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute("content", theme === "light" ? "#f6f7fb" : "#0b1020");
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  cycle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: "dark",
  setTheme: (theme) => {
    apply(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* storage unavailable */
    }
    set({ theme });
  },
  cycle: () => {
    const order: Theme[] = ["dark", "light", "glass"];
    const next = order[(order.indexOf(get().theme) + 1) % order.length]!;
    get().setTheme(next);
  },
}));

export function initializeTheme(): void {
  const theme = readInitial();
  apply(theme);
  useThemeStore.setState({ theme });
}
